import { useState, useRef, useEffect } from "react";
import { Box, Button, Paper } from "@mui/material";
import CadastroHeader from "../../components/CadastroHeader/CadastroHeader";
import ButtonPages from "../../components/ButtonPages/ButtonPages";
import styles from "../../styles/styles";
import FiltersBar from "./FiltersBar";
import TelemetryList from "./TelemetryList";
import { useTelemetrySearch } from "../../app/hooks/useTelemetrySearch";
import { useTrens } from "../../app/hooks/useTrens";
import { useMinhaLinhaOperador } from "../../app/hooks/useMinhaLinhaOperador";
import { useAuth } from "../../contexts/AuthContext";
import "./TelemetrySearch.css";
import { exportFullTelemetry } from "../../util/exportTelemetryFull";
import SemPermissao from "../../components/SemPermissao/SemPermissao";
import { usePermissao } from "../../app/hooks/UsePermissao";
import Modal from "../../components/Modal/Modal";
import { PARAMETER_MAP } from "../../util/PARAMETER_MAP";

function ProcessingModal({ progress, onCancel }) {

  const safeProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="processing-backdrop">
      <div className="processing-card">

        <div className="processing-title">
          Gerando CSV
        </div>

        <div className="processing-bar">
          <div
            className="processing-bar-fill"
            style={{ width: `${safeProgress}%` }}
          />
        </div>

        <div className="processing-hint">
          {safeProgress}% concluído
          <br />
          Processando todos os registros
        </div>

        <Button
          variant="outlined"
          color="error"
          sx={{ mt: 2 }}
          onClick={onCancel}
        >
          Cancelar
        </Button>

      </div>
    </div>
  );
}

export default function TelemetrySearch() {

  const { temPermissao } = usePermissao();

  const {
    data,
    total,
    page,
    setPage,
    pageSize,
    loading,
    search
  } = useTelemetrySearch();

  const { userInfo } = useAuth();
  const { idLinha: operadorLinhaId } = useMinhaLinhaOperador(userInfo);

  const { trens, error: trensError } = useTrens();

  const trensDaLinha = operadorLinhaId
    ? trens.filter((t) => t.linhaId === operadorLinhaId)
    : trens;

  const tremNomeMap = trensDaLinha.reduce((map, t) => {
    map[String(t.identificadorTrem)] = t.nomeCodigo;
    return map;
  }, {});

  const flattenedData = (data || [])
    .flatMap((trem) => {

      const mensagens = trem?.data || [];

      return mensagens.map((msg) => {

        const timestamp = msg.messageTimestamp ?? msg.message_timestamp ?? null;

        const normalizedTimestamp =
          timestamp && timestamp < 1e12
            ? timestamp * 1000
            : timestamp;

        const tremId = msg.tremId ?? trem.idTrem ?? "-";

        return {
          id: msg.id ?? `${msg.messageId}-${msg.parameterId}`,
          tremId,
          tremNome: tremNomeMap[String(tremId)] ?? tremId,
          carro: msg.carro ?? "-",
          category: msg.category ?? null,
          parameterId: msg.parameterId ?? "-",

          parameterName:
            PARAMETER_MAP?.[String(msg.parameterId)]?.name ??
            msg.parameterId ??
            "-",
          parameterValue: msg.parameterValue ?? "-",
          messageTimestamp: normalizedTimestamp,
          createdAt: msg.createdAt ?? null
        };
      });

    })
    .sort((a, b) => {
      return (b.messageTimestamp || 0) - (a.messageTimestamp || 0);
    });
  const hasResults =
    data?.length > 0 &&
    data.some(trem => trem.data?.length > 0);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastFilters, setLastFilters] = useState(null);
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  useEffect(() => {
    if (trensError) {
      setErrorModal({ show: true, message: trensError });
    }
  }, [trensError]);

  const exportExecutionIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  const sanitizeFilters = (filters) => {
    const toNonNegative = (value) => {
      if (value === null || value === undefined || value === "") return null;

      const num = Number(value);
      if (Number.isNaN(num)) return null;

      return Math.max(0, num);
    };

    return {
      ...filters,
      tremId: toNonNegative(filters.tremId),
      carro: toNonNegative(filters.carro),
      parameterId: toNonNegative(filters.parameterId),
      category: toNonNegative(filters.category)
    };
  };

  if (!temPermissao("Trem.VerLogs")) {
    return (
      <Box sx={styles.pageWrapper}>
        <Box component="main" sx={styles.mainContent}>
          <Paper elevation={0} sx={styles.card}>
            <CadastroHeader
              title="Histórico de Telemetria"
              backRoute="/dashboard"
            />
            <SemPermissao mensagem="Você não tem permissão para acessar o histórico de telemetria." />
          </Paper>
        </Box>
      </Box>
    );
  }

  const handleExport = () => {

    if (processing) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    exportExecutionIdRef.current += 1;
    const executionId = exportExecutionIdRef.current;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setProgress(0);
    setProcessing(true);

    exportFullTelemetry({
      filters: lastFilters,
      signal: controller.signal,
      executionId,
      getCurrentExecutionId: () => exportExecutionIdRef.current,

      setProgress: (value) => {
        const safe = Math.min(100, Math.max(0, value));
        setProgress(safe);
      },

      setProcessing,
      onError: (msg) => setErrorModal({ show: true, message: msg })
    });

  };

  const handleCancel = () => {

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    exportExecutionIdRef.current += 1;

    setProcessing(false);
    setProgress(0);

  };

  return (
    <>
      <Modal
        show={errorModal.show}
        type="error"
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />

      <Box sx={styles.pageWrapper}>


        <Box component="main" sx={styles.mainContent}>

          <Paper elevation={0} sx={styles.card}>

            <CadastroHeader
              title="Histórico de Telemetria"
              backRoute="/dashboard"
            />

            <div className="card-container">

              <div className="card">

                <FiltersBar
                  trens={trensDaLinha}
                  onSearch={(filters) => {

                    const sanitized = sanitizeFilters(filters);

                    setLastFilters(sanitized);
                    setHasSearched(true);
                    setProgress(0);
                    setProcessing(false);

                    setPage(1);
                    search(sanitized);

                  }}
                />

                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>

                  <Button
                    variant="contained"
                    disabled={
                      !lastFilters ||
                      processing ||
                      loading ||
                      !hasResults
                    }
                    onClick={handleExport}
                  >
                    Exportar CSV completo
                  </Button>

                </div>

                <div className="section-title">

                  <span>Resultados</span>

                  <span>
                    {loading
                      ? "Buscando..."
                      : hasResults
                        ? `${total} registros encontrados`
                        : hasSearched
                          ? "0 registros encontrados"
                          : ""}
                  </span>

                </div>

                {loading && (
                  <div style={{ padding: 20, color: "#6b7280" }}>
                    Carregando dados…
                  </div>
                )}

                {!loading && !hasResults && (
                  <div style={{
                    padding: 40,
                    textAlign: "center",
                    color: "#6b7280",
                    opacity: 0.8
                  }}>
                    Nenhum registro encontrado com os filtros aplicados.
                  </div>
                )}

                {!loading && flattenedData.length > 0 && (
                  <TelemetryList data={flattenedData} />
                )}

                {!loading && hasResults && (
                  <ButtonPages
                    paginaAtual={page}
                    setPaginaAtual={setPage}
                    totalCount={total}
                    itensPorPagina={pageSize}
                  />
                )}

              </div>

            </div>

          </Paper>

        </Box>

        {processing && (
          <ProcessingModal
            progress={progress}
            onCancel={handleCancel}
          />
        )}

      </Box>
    </>
  );
}